import type { IProviderConfig } from "@/account/account-models";
import reUtf16UnpairedSurrogate from "@stdlib/regexp-utf16-unpaired-surrogate";
import emojiRegexCreator from "emoji-regex";
import { toHumanString } from "human-readable-numbers";
import { defined } from "./variable-evaluation";

export function toTitleCase(str: string) {
  return str
    .replace(/-/g, " ")
    .replace(/_/g, " ")
    .split(" ")
    .map((word) =>
      word.length > 0
        ? `${word[0].toLocaleUpperCase()}${word.substring(1)}`
        : word
    )
    .join(" ");
}

export function capitalizeFirstLetter(str: string) {
  if (typeof str === "string") {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
  return "";
}

export function formatVideoDuration(videoLength?: number | string | null) {
  if (videoLength === 0) {
    return "0s";
  }
  if (
    !videoLength ||
    (typeof videoLength !== "string" && typeof videoLength !== "number") ||
    isNaN(Number(videoLength))
  ) {
    return "";
  }

  if (typeof videoLength !== "number") {
    videoLength = Number(videoLength);
  }
  videoLength = Math.round(videoLength);
  if (videoLength < 0) {
    return "0s";
  }
  if (videoLength === 0) {
    videoLength = 1;
  }

  const minutes = Math.floor(videoLength / 60);
  const seconds = videoLength % 60;
  return `${minutes > 0 ? `${minutes}m` : ""}${
    minutes >= 1 && seconds < 10 ? "0" + seconds : seconds
  }s`;
}

function formatString(input: string): string {
  const formattedString = ` ${input} ` // to handle whole word search, e.g. ' supe '
    .toLocaleLowerCase() // lower case only
    .replace(/[$&+,:;=?#|/\\'<>.^*()%!-]/g, " "); // remove special characters
  return formattedString;
}

function isWordsInSentence(sentence: string, words: string[]): boolean {
  const formattedSentence = formatString(sentence);
  return words.some((word) => formattedSentence.includes(formatString(word)));
}

function getDirtyWords(providerConfig: IProviderConfig): string[] {
  let keywords: string[] = [];
  if (defined(providerConfig) && defined(providerConfig.greeting_keywords)) {
    keywords = providerConfig.greeting_keywords!.split(",");
  }
  return keywords;
}

function getCompetingAppWords(providerConfig: IProviderConfig): string[] {
  const keywords: string[] = [];
  if (defined(providerConfig)) {
    if (defined(providerConfig.payment_keywords)) {
      keywords.push(...providerConfig.payment_keywords!.split(","));
    }
    if (defined(providerConfig.app_keywords)) {
      keywords.push(...providerConfig.app_keywords!.split(","));
    }
    if (defined(providerConfig.global_keywords)) {
      keywords.push(...providerConfig.global_keywords!.split(","));
    }
  }
  return keywords;
}

export const urlMatchingExpression =
  /(https?:\/\/)?(w){0,3}\.?[-a-z0-9]{1,256}\.(com|net|org|eu|info|co\.uk)\b([-a-z0-9()@:%_+~#?&//=]*)?/gi;

export const numberWithCommas = (x: number | string) => {
  const parts = x.toString().split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return parts.join(".");
};

export function toFixedWithRounding(
  num: number,
  fractionDigits?: number
): string {
  if (typeof num !== "number") {
    return "NaN";
  }
  if ((num > 0 && num < 0.000001) || (num < 0 && num > -0.000001)) {
    // HACK: below this js starts to turn numbers into exponential form like 1e-7.
    // This gives wrong results so we are just changing the original number to 0 here
    // as we don't need such small numbers anyway.
    num = 0;
  }
  return num.toFixed(fractionDigits);
}

export function toFixedWithoutRounding(
  num: number,
  fractionDigits?: number
): string {
  if (typeof num !== "number") {
    return "NaN";
  }
  if ((num > 0 && num < 0.000001) || (num < 0 && num > -0.000001)) {
    // HACK: below this js starts to turn numbers into exponential form like 1e-7.
    // This gives wrong results so we are just changing the original number to 0 here
    // as we don't need such small numbers anyway.
    num = 0;
  }
  // prettier-ignore
  const re = new RegExp('^-?\\d+(?:.\\d{0,' + (fractionDigits || -1) + '})?');
  return Number(num.toString().match(re)![0]).toFixed(fractionDigits);
}

export function creditsToDollars(credits: number) {
  const fixedWithoutRound = toFixedWithoutRounding(credits * 0.1, 2);
  if (fixedWithoutRound[0] === "-") {
    return `-$${fixedWithoutRound.slice(1)}`;
  }
  return `$${fixedWithoutRound}`;
}

export function censor(str: string): string {
  if (str.length > 4) {
    return str.replace(str.substring(2, str.length - 2), "***");
  }
  if (str.length > 2) {
    return str.replace(str.substring(1, str.length - 1), "***");
  }
  return "***";
}

export function censorEmail(email?: string): string {
  if (!email) {
    return "";
  }

  const emailParts = email.split("@");
  const censorPart = emailParts[0];
  const censorPartLength = censorPart.length;
  if (censorPartLength >= 8) {
    return (
      censorPart.substring(0, 3) +
      "*".repeat(censorPartLength - 3) +
      "@" +
      emailParts[1]
    );
  }
  if (censorPartLength >= 4) {
    return (
      censorPart.substring(0, 2) +
      "*".repeat(censorPartLength - 2) +
      "@" +
      emailParts[1]
    );
  }
  if (censorPartLength >= 2) {
    return (
      censorPart.substring(0, 1) +
      "*".repeat(censorPartLength - 1) +
      "@" +
      emailParts[1]
    );
  }
  return `*@${emailParts[1]}`;
}

export function censorCryptoWalletAddress(walletAddress?: string): string {
  if (!walletAddress) {
    return "";
  }

  if (walletAddress.length < 10) {
    return walletAddress; // too short to censor
  }

  return "*".repeat(4) + walletAddress.slice(-6);
}

export function ordinal(i: number): string {
  const suffixes: string[] = [
    "th",
    "st",
    "nd",
    "rd",
    "th",
    "th",
    "th",
    "th",
    "th",
    "th",
  ];
  switch (i % 100) {
    case 11:
    case 12:
    case 13:
      return "th";
    default:
      return suffixes[i % 10];
  }
}

export function numberToHumanReadable(num: number): string {
  return toHumanString(num);
}

export function addUrlParam(
  urlString: string,
  paramName: string,
  paramValue: string
): string {
  return `${urlString}${
    urlString.match(/[?]/g) ? "&" : "?"
  }${paramName}=${encodeURI(paramValue)}`;
}

export function replaceUtf16UnpairedSurrogates(str: string): string {
  const RE_UTF16_UNPAIRED_SURROGATE = reUtf16UnpairedSurrogate();
  return str.replace(RE_UTF16_UNPAIRED_SURROGATE, "�");
}

export function isEmoji(text?: string): boolean {
  if (!text) return false;

  const emojiRegex = emojiRegexCreator();
  return emojiRegex.test(text);
}

export function isTextSingleEmoji(text?: string): boolean {
  if (!text) return false;

  if (typeof Intl === "undefined" || !("Segmenter" in Intl)) {
    return false;
  }

  const segmenter = new Intl.Segmenter(undefined, {
    granularity: "grapheme",
  });

  const segments = segmenter.segment(text);

  const first = segments.containing(0);
  if (!first) return false;

  // Ensure the first grapheme spans the whole string
  if (first.index !== 0 || first.segment.length !== text.length) {
    return false;
  }

  const emojiRegex = emojiRegexCreator();
  return emojiRegex.test(first.segment);
}

export function isSpecialCharacters(value: string): boolean {
  return /[:/~!@#$%^&*()[\]{};|?._=<>\\]/.test(value);
}

export function isUserAskingExternalPayment(
  text: string,
  providerConfig: IProviderConfig
): boolean {
  let keywords: string[] = [];
  if (defined(providerConfig) && defined(providerConfig.payment_keywords)) {
    keywords = providerConfig.payment_keywords!.split(",");
  }
  return isWordsInSentence(text, keywords);
}

export function isUserUsingForbiddenKeyword(
  text: string,
  providerConfig: IProviderConfig
): boolean {
  let keywords: string[] = [];
  if (defined(providerConfig) && defined(providerConfig.global_keywords)) {
    keywords = providerConfig.global_keywords!.split(",");
  }
  return isWordsInSentence(text, keywords);
}

export function isUsingDirtyWords(
  text: string,
  providerConfig: IProviderConfig
): boolean {
  return isWordsInSentence(text, getDirtyWords(providerConfig));
}

export function isUsingCompetingAppWords(
  text: string,
  providerConfig: IProviderConfig
): boolean {
  return isWordsInSentence(text, getCompetingAppWords(providerConfig));
}

/**
 * Sanitizes username input by removing whitespace and converting to lowercase
 * Does NOT remove @ symbol - that should be handled separately for processing
 */
export function sanitizeUsername(username: string): string {
  return username.toLocaleLowerCase().replace(/\s/g, "");
}

/**
 * Extracts the actual username from sanitized input by removing leading @ symbol
 * Use this when processing/validating the username value
 * Assumes input has already been sanitized (lowercase, no whitespace)
 */
export function extractUsername(sanitizedInput: string): string {
  // Strip leading @ symbol if present
  if (sanitizedInput.startsWith("@")) {
    return sanitizedInput.substring(1);
  }

  return sanitizedInput;
}

/**
 * Validates username according to API rules:
 * - Can only contain letters, numbers and hyphens
 * - Must start with letters or numbers (not hyphens)
 * - Must be at least 3 characters long
 * - Must be at most 30 characters long
 */
export function validateUsername(username: string): {
  isValid: boolean;
  errorMessage: string;
} {
  if (!username || username.length === 0) {
    return { isValid: false, errorMessage: "Username is required" };
  }

  if (username.length < 3) {
    return {
      isValid: false,
      errorMessage: "Username should contain at least 3 characters",
    };
  }

  if (username.length > 30) {
    return {
      isValid: false,
      errorMessage: "Username cannot exceed 30 characters",
    };
  }

  // Check if username contains only allowed characters (letters, numbers, hyphens)
  if (!/^[a-z0-9-]+$/i.test(username)) {
    return {
      isValid: false,
      errorMessage: "Usernames can only contain letters, numbers and hyphens",
    };
  }

  // Check if username starts with letter or number (not hyphen)
  if (!/^[a-z0-9]/i.test(username)) {
    return {
      isValid: false,
      errorMessage: "Usernames must start with letters or numbers",
    };
  }

  // Check if username ends with letter or number (not hyphen)
  if (!/[a-z0-9]$/i.test(username)) {
    return {
      isValid: false,
      errorMessage: "Usernames must end with letters or numbers",
    };
  }

  return { isValid: true, errorMessage: "" };
}
